import React, { useMemo } from 'react';

import { Bar } from '@ant-design/plots';
import { Empty } from 'antd';

import type { EnterpriseResolver } from '../schemas';

type Row = { enterpriseId: number; orders: number; suivi: number };

type Props = {
  rows: Row[];
  metric: 'orders' | 'suivi';
  resolveEnterprise?: EnterpriseResolver;
};

const FILL = {
  orders: '#1677ff',
  suivi: '#fa8c16',
} as const;

export const EnterpriseBarChart = ({ rows, metric, resolveEnterprise }: Props) => {
  const data = useMemo(() => {
    const filtered = rows.filter((r) => r[metric] > 0);
    const sorted = [...filtered].sort((a, b) => b[metric] - a[metric]).slice(0, 12);
    return sorted.map((r) => ({
      label: resolveEnterprise ? resolveEnterprise(r.enterpriseId) : `#${r.enterpriseId}`,
      value: r[metric],
    }));
  }, [rows, metric, resolveEnterprise]);

  if (!data.length) {
    return <Empty description="No enterprise activity in this period" />;
  }

  // Auto-grow chart height with the number of bars so labels never collide.
  const height = Math.min(420, 56 + data.length * 30);

  return (
    <Bar
      data={data}
      xField="value"
      yField="label"
      autoFit
      height={height}
      sort={{ reverse: true, by: 'x' }}
      style={{
        fill: FILL[metric],
        radiusTopRight: 4,
        radiusBottomRight: 4,
      }}
      axis={{
        x: { labelFormatter: (v: number) => String(v), gridLineDash: [2, 2] },
        y: { tick: false },
      }}
      label={{
        text: 'value',
        position: 'right',
        style: { fill: '#262626', fontWeight: 600 },
      }}
      tooltip={{ title: 'label', items: [{ field: 'value' }] }}
    />
  );
};
